import { useMemo } from 'react';
import { osloDateString } from '@/lib/oslo-time';
import { runMaaltallChecks, type CheckFinding } from '@/lib/maaltall-checks';
import { useActiveProjectsInvoiceSummary, VAT_MULTIPLIER } from '@/hooks/use-active-projects-invoice-summary';
import { useOffersInPipe } from '@/hooks/use-offers-in-pipe';
import { useBillingRates } from '@/hooks/use-billing-rates';
import { useMaaltallCheckInputs } from '@/hooks/use-maaltall-check-inputs';

/**
 * Funnene i «Dette bør du se på» (VIB-2991). Delt av widgeten og tall-agenten
 * (VIB-2992), så agenten foreslår bare det sjekkene faktisk finner.
 */
export function useMaaltallFindings(year: number): { findings: CheckFinding[] | null; error: boolean } {
  const active = useActiveProjectsInvoiceSummary();
  const pipe = useOffersInPipe(true);
  const billing = useBillingRates(year);
  const db = useMaaltallCheckInputs(year);

  const findings = useMemo(() => {
    if (!active.data || !billing.data || !db.data || pipe.isLoading) return null;
    const offers = new Map<string, { id: string; title: string; customerName: string | null; bfKr: number }>();
    for (const p of pipe.pipes) {
      for (const o of p.offers) {
        if (o.probability != null) continue;
        const row = offers.get(o.id) ?? { id: o.id, title: o.title, customerName: o.customerName, bfKr: 0 };
        row.bfKr += o.bfKr;
        offers.set(o.id, row);
      }
    }
    return runMaaltallChecks({
      today: osloDateString(new Date()),
      activeProjects: active.data.rows.map((r) => ({
        id: r.id,
        name: r.name,
        projectCode: r.projectCode,
        advisorName: r.advisorName,
        deadline: r.deadline,
        rest: r.gjenstaarInkl / VAT_MULTIPLIER,
        bfEstimated: r.bfEstimated,
      })),
      uncategorizedOffers: [...offers.values()],
      employees: billing.data.employees,
      ...db.data,
    });
  }, [active.data, billing.data, db.data, pipe.isLoading, pipe.pipes]);

  return { findings, error: !!(active.error || billing.error || db.error) };
}
